import type { DocsLocale, DocsPage, DocsPageResolved, DocsShortcut } from './types'
import { getDocsSections, getSection } from './registry'
import { getDocsPage, getDocsPages, getDocsPagesForSection } from './content'
import { DOCS_SCREENSHOTS } from './screenshots'
import { FEATURE_REGISTRY, getFeature, type FeatureId } from '@/lib/features/registry'

const SHORTCUT_LABEL_OVERRIDES_EN: Record<string, string> = {
  'placement-video': 'Create a new shot on the canvas.',
  'placement-image': 'Place an image on the canvas.',
  'placement-text': 'Place a text annotation on the canvas.',
  'placement-bin': 'Create a bin for related work.',
  'placement-variable': 'Create a reusable prompt variable.',
  'placement-set': 'Place a set on the canvas.',
  'placement-audio': 'Place audio on the canvas.',
  'selection-click': 'Select a node.',
  'selection-select-all': 'Select all nodes on the canvas.',
  'selection-escape': 'Clear the selection.',
  'camera-fit-all': 'Fit all nodes in the viewport.',
  'camera-zoom-100': 'Reset zoom to 100%.',
  'inspector-close': 'Close the inspector.',
  'clipboard-copy': 'Copy selected nodes.',
  'clipboard-paste': 'Paste copied nodes.',
  'duplicate-node': 'Duplicate selected nodes in place.',
  undo: 'Undo the last action.',
  redo: 'Redo the last undone action.',
  'delete-selection': 'Delete selected nodes.',
  'rating-favorite': 'Mark the selected shot as a favorite.',
  'rating-reject': 'Mark the selected shot as rejected.',
  'rename-inline': 'Rename the selected node inline.',
  'drag-node': 'Drag to reposition nodes.',
  'playback-toggle': 'Play or pause the selected shot.',
  'playback-forward': 'Scrub forward.',
  'playback-backward': 'Scrub backward.',
  'playback-stop': 'Stop playback.',
}

const SHORTCUT_LABEL_OVERRIDES_JA: Record<string, string> = {
  'placement-video': 'キャンバスに新しいショットを作成します。',
  'placement-image': 'キャンバスに画像を配置します。',
  'placement-text': 'キャンバスにテキスト注釈を配置します。',
  'placement-bin': '関連する作業をまとめるビンを作成します。',
  'placement-variable': '再利用できるプロンプト変数を作成します。',
  'placement-set': 'キャンバスにセットを配置します。',
  'placement-audio': 'キャンバスに音声を配置します。',
  'selection-click': 'ノードを選択します。',
  'selection-select-all': 'キャンバス上のすべてのノードを選択します。',
  'selection-escape': '選択を解除します。',
  'camera-fit-all': 'すべてのノードが表示されるようにビューポートを合わせます。',
  'camera-zoom-100': 'ズームを100%に戻します。',
  'inspector-close': 'インスペクターを閉じます。',
  'clipboard-copy': '選択したノードをコピーします。',
  'clipboard-paste': 'コピーしたノードを貼り付けます。',
  'duplicate-node': '選択したノードをその場で複製します。',
  undo: '直前の操作を取り消します。',
  redo: '取り消した操作をやり直します。',
  'delete-selection': '選択したノードを削除します。',
  'rating-favorite': '選択したショットをお気に入りにします。',
  'rating-reject': '選択したショットをリジェクトにします。',
  'rename-inline': '選択したノードの名前をインラインで変更します。',
  'drag-node': 'ドラッグしてノードの位置を変更します。',
  'playback-toggle': '選択したショットを再生または一時停止します。',
  'playback-forward': '前方にスクラブします。',
  'playback-backward': '後方にスクラブします。',
  'playback-stop': '再生を停止します。',
}

// Initial LLM-assisted Arabic shortcut labels. Koh reviews these in Phase 6
// before public release.
const SHORTCUT_LABEL_OVERRIDES_AR: Record<string, string> = {
  'placement-video': 'إنشاء لقطة جديدة على الكانفاس.',
  'placement-image': 'وضع صورة على الكانفاس.',
  'placement-text': 'وضع تعليق نصي على الكانفاس.',
  'placement-bin': 'إنشاء حاوية للأعمال ذات الصلة.',
  'placement-variable': 'إنشاء متغير قابل لإعادة الاستخدام في المطالبات.',
  'placement-set': 'وضع مجموعة على الكانفاس.',
  'placement-audio': 'وضع صوت على الكانفاس.',
  'selection-click': 'تحديد عقدة.',
  'selection-select-all': 'تحديد جميع العقد على الكانفاس.',
  'selection-escape': 'إلغاء التحديد.',
  'camera-fit-all': 'ملاءمة جميع العقد داخل نافذة العرض.',
  'camera-zoom-100': 'إعادة تعيين التكبير إلى 100٪.',
  'inspector-close': 'إغلاق المفتش.',
  'clipboard-copy': 'نسخ العقد المحددة.',
  'clipboard-paste': 'لصق العقد المنسوخة.',
  'duplicate-node': 'تكرار العقد المحددة في مكانها.',
  undo: 'التراجع عن آخر إجراء.',
  redo: 'إعادة الإجراء الذي تم التراجع عنه.',
  'delete-selection': 'حذف العقد المحددة.',
  'rating-favorite': 'تمييز اللقطة المحددة كمفضلة.',
  'rating-reject': 'تمييز اللقطة المحددة كمرفوضة.',
  'rename-inline': 'إعادة تسمية العقدة المحددة مباشرة.',
  'drag-node': 'اسحب لتغيير موضع العقد.',
  'playback-toggle': 'تشغيل أو إيقاف اللقطة المحددة مؤقتًا.',
  'playback-forward': 'تقديم سريع.',
  'playback-backward': 'ترجيع.',
  'playback-stop': 'إيقاف التشغيل.',
}

const SHORTCUT_LABEL_OVERRIDES_ES: Record<string, string> = {
  'placement-video': 'Crea un nuevo shot en el lienzo.',
  'placement-image': 'Coloca una imagen en el lienzo.',
  'placement-text': 'Coloca una anotación de texto en el lienzo.',
  'placement-bin': 'Crea un bin para agrupar trabajo relacionado.',
  'placement-variable': 'Crea una variable de prompt reutilizable.',
  'placement-set': 'Coloca un set en el lienzo.',
  'placement-audio': 'Coloca audio en el lienzo.',
  'selection-click': 'Selecciona un nodo.',
  'selection-select-all': 'Selecciona todos los nodos del lienzo.',
  'selection-escape': 'Limpia la selección.',
  'camera-fit-all': 'Ajusta todos los nodos a la vista.',
  'camera-zoom-100': 'Restablece el zoom al 100%.',
  'inspector-close': 'Cierra el inspector.',
  'clipboard-copy': 'Copia los nodos seleccionados.',
  'clipboard-paste': 'Pega los nodos copiados.',
  'duplicate-node': 'Duplica los nodos seleccionados en su lugar.',
  undo: 'Deshace la última acción.',
  redo: 'Rehace la última acción deshecha.',
  'delete-selection': 'Elimina los nodos seleccionados.',
  'rating-favorite': 'Marca el shot seleccionado como favorito.',
  'rating-reject': 'Marca el shot seleccionado como descartado.',
  'rename-inline': 'Renombra el nodo seleccionado en línea.',
  'drag-node': 'Arrastra para reposicionar nodos.',
  'playback-toggle': 'Reproduce o pausa el shot seleccionado.',
  'playback-forward': 'Avanza el scrub.',
  'playback-backward': 'Retrocede el scrub.',
  'playback-stop': 'Detiene la reproducción.',
}

const SHORTCUT_LABEL_OVERRIDES_KO: Record<string, string> = {
  'placement-video': '캔버스에 새 샷을 만듭니다.',
  'placement-image': '캔버스에 이미지를 배치합니다.',
  'placement-text': '캔버스에 텍스트 주석을 배치합니다.',
  'placement-bin': '관련 작업을 묶을 빈을 만듭니다.',
  'placement-variable': '재사용 가능한 프롬프트 변수를 만듭니다.',
  'placement-set': '캔버스에 세트를 배치합니다.',
  'placement-audio': '캔버스에 오디오를 배치합니다.',
  'selection-click': '노드를 선택합니다.',
  'selection-select-all': '캔버스의 모든 노드를 선택합니다.',
  'selection-escape': '선택을 해제합니다.',
  'camera-fit-all': '모든 노드가 보이도록 뷰포트를 맞춥니다.',
  'camera-zoom-100': '줌을 100%로 되돌립니다.',
  'inspector-close': '인스펙터를 닫습니다.',
  'clipboard-copy': '선택한 노드를 복사합니다.',
  'clipboard-paste': '복사한 노드를 붙여 넣습니다.',
  'duplicate-node': '선택한 노드를 그 자리에서 복제합니다.',
  undo: '마지막 작업을 취소합니다.',
  redo: '취소한 작업을 다시 실행합니다.',
  'delete-selection': '선택한 노드를 삭제합니다.',
  'rating-favorite': '선택한 샷을 즐겨찾기에 추가합니다.',
  'rating-reject': '선택한 샷을 리젝트로 표시합니다.',
  'rename-inline': '선택한 노드의 이름을 인라인으로 바꿉니다.',
  'drag-node': '드래그해 노드 위치를 옮깁니다.',
  'playback-toggle': '선택한 샷을 재생하거나 일시 정지합니다.',
  'playback-forward': '앞으로 스크럽합니다.',
  'playback-backward': '뒤로 스크럽합니다.',
  'playback-stop': '재생을 멈춥니다.',
}

const SHORTCUT_LABEL_OVERRIDES = {
  en: SHORTCUT_LABEL_OVERRIDES_EN,
  ja: SHORTCUT_LABEL_OVERRIDES_JA,
  ar: SHORTCUT_LABEL_OVERRIDES_AR,
  es: SHORTCUT_LABEL_OVERRIDES_ES,
  ko: SHORTCUT_LABEL_OVERRIDES_KO,
} satisfies Record<DocsLocale, Record<string, string>>

function extractShortcuts(featureIds: string[], locale: DocsLocale): DocsShortcut[] {
  const shortcuts: DocsShortcut[] = []
  const seen = new Set<string>()
  const overrides = SHORTCUT_LABEL_OVERRIDES[locale]

  for (const id of featureIds) {
    if (!(id in FEATURE_REGISTRY)) continue
    const feature = getFeature(id as FeatureId)
    if (!feature.shortcut || seen.has(feature.shortcut)) continue
    seen.add(feature.shortcut)

    const label = overrides[id] ?? SHORTCUT_LABEL_OVERRIDES_EN[id] ?? feature.tip ?? humanizeFeatureId(id)

    shortcuts.push({ keys: feature.shortcut, label })
  }

  return shortcuts
}

function humanizeFeatureId(id: string): string {
  return (
    id
      .replace(/[-_]/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase())
      .replace(/\.$/, '') + '.'
  )
}

export function resolveDocsPage(page: DocsPage, locale: DocsLocale = 'en'): DocsPageResolved {
  const section = getSection(page.section, locale)
  const featureIds = page.featureIds ?? []

  const shortcuts = extractShortcuts(featureIds, locale)

  const sectionPages = getDocsPagesForSection(page.section, locale)
  const relatedPages = sectionPages.filter(p => p.slug !== page.slug).map(p => ({ slug: p.slug, title: p.title }))

  return {
    ...page,
    sectionTitle: section?.title ?? page.section,
    shortcuts,
    glossaryTerms: [],
    relatedPages,
    screenshots: page.screenshots ?? DOCS_SCREENSHOTS[page.slug] ?? [],
    videoUrl: null,
    heroScreenshot: null,
  }
}

export function resolveAllDocsPages(locale: DocsLocale = 'en'): DocsPageResolved[] {
  return getDocsPages(locale).map(page => resolveDocsPage(page, locale))
}

export function getResolvedDocsPage(slug: string, locale: DocsLocale = 'en'): DocsPageResolved | undefined {
  const page = getDocsPage(slug, locale)
  if (!page) return undefined
  return resolveDocsPage(page, locale)
}

export function getSectionWithPages(locale: DocsLocale = 'en'): Array<{
  id: string
  title: string
  pages: DocsPageResolved[]
}> {
  return getDocsSections(locale)
    .map(section => ({
      id: section.id,
      title: section.title,
      pages: getDocsPagesForSection(section.id, locale).map(page => resolveDocsPage(page, locale)),
    }))
    .filter(group => group.pages.length > 0)
}
